import { useCallback, useMemo } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  authState,
  currentUserSelector,
  getToken,
  isAuthenticatedSelector,
  setToken,
  clearToken,
} from '../../store/auth'
import { AuthenticatedUser } from '../../clients/kyousou_api_client'
import {
  ApiErrorResponse,
  getKyousouApiClient,
  handleError,
} from '../../services/network'
import { useSnackBar } from '../../providers/snack_bar_provider'
import { useLoading } from '../../providers/loading_provider'

export interface LoginCredentials {
  email: string
  password: string
}

export interface TokenLoginCredentials {
  token: string
  userId: string
  key: string
}

export interface ResetPasswordData {
  token: string
  email: string
  password: string
  password_confirmation: string
}

// privaterouteの中で使える
export const useAuthAuthorized = () => {
  const user = useRecoilValue(currentUserSelector)

  if (user === null) {
    throw new Error('ログインしていません')
  }

  return { user }
}

export const useAuth = () => {
  const [auth, setAuth] = useRecoilState(authState)
  const user = useRecoilValue(currentUserSelector)
  const isAuthenticated = useRecoilValue(isAuthenticatedSelector)
  const apiClient = useMemo(() => getKyousouApiClient(), [])
  const { showLoading, hideLoading } = useLoading()
  const { showSnackBar } = useSnackBar()

  /**
   * 認証情報を初期化する
   */
  const initialize = useCallback(async () => {
    // 既に認証済みなら何もしない
    if (auth.initialized) {
      return
    }

    setAuth((prev) => ({ ...prev, loading: true }))

    const token = getToken()
    if (token) {
      const responseUser = await apiClient.auth
        .checkCreate()
        .then((res) => res.data.user)
        .catch((e: ApiErrorResponse) => {
          console.error(e)
          return null
        })

      if (responseUser) {
        setAuth({
          isAuthenticated: true,
          user: responseUser,
          token,
          loading: false,
          initialized: true,
        })
        return
      }
    }

    setAuth({
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false,
      initialized: true,
    })
  }, [setAuth, auth.initialized, apiClient])

  /**
   * メールアドレスでログインする
   * @returns ログイン成功かどうか
   */
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<boolean> => {
      setAuth((prev) => ({ ...prev, loading: true }))

      const res = await apiClient.auth
        .loginEmailCreate(credentials)
        .catch((e: ApiErrorResponse) => {
          handleError(e, showSnackBar)
          setAuth((prev) => ({ ...prev, loading: false }))
          return null
        })

      if (res !== null) {
        setToken(res.data.token)
        setAuth({
          isAuthenticated: true,
          user: res.data.user,
          token: res.data.token,
          loading: false,
          initialized: true,
        })
        window.localStorage.setItem('ACCESS-TOKEN', res.data.token)
        return true
      }
      return false
    },
    [setAuth, apiClient, showSnackBar]
  )

  /**
   * トークンでログインする
   * @returns ログイン成功かどうか
   */
  const loginWithToken = useCallback(
    async (credentials: TokenLoginCredentials): Promise<boolean> => {
      setAuth((prev) => ({ ...prev, loading: true }))

      const res = await apiClient.auth
        .loginTokenCreate(credentials)
        .catch((e: ApiErrorResponse) => {
          handleError(e, showSnackBar)
          setAuth((prev) => ({ ...prev, loading: false }))
          return null
        })

      if (res !== null) {
        setToken(res.data.token)
        setAuth({
          isAuthenticated: true,
          user: res.data.user,
          token: res.data.token,
          loading: false,
          initialized: true,
        })
        window.localStorage.setItem('ACCESS-TOKEN', res.data.token)
        return true
      }
      return false
    },
    [setAuth, apiClient, showSnackBar]
  )

  // ログアウト
  const logout = useCallback(async () => {
    showLoading()
    await apiClient.auth.logoutCreate().catch((e: ApiErrorResponse) => {
      console.error(e)
    })

    clearToken()

    setAuth({
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false,
      initialized: true,
    })
    hideLoading()
  }, [setAuth, apiClient, showLoading, hideLoading])

  // ユーザー情報更新
  const updateUser = useCallback(
    (updatedUser: AuthenticatedUser) => {
      setAuth((prev) => ({
        ...prev,
        user: updatedUser,
      }))
    },
    [setAuth]
  )

  return {
    // 状態
    auth,
    user,
    isAuthenticated,
    loading: auth.loading,
    initialized: auth.initialized,

    // アクション
    initialize,
    login,
    loginWithToken,
    logout,
    updateUser,
  }
}
